import { AfterViewInit, Component, Inject, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { DialogPromptAiComponent } from 'src/app/components/dialog-prompt-ai/dialog-prompt-ai.component';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { KPI, KPIService, Stdr } from 'src/app/services/KPI/kpi.service';
import { BilancioService } from 'src/app/services/bilancio/bilancio.service';
import { PianoService } from 'src/app/services/piano/piano.service';
import { Settore, SettoriService } from 'src/app/services/settori/settori.service';
import { SintesiService } from 'src/app/services/sintesi/sintesi.service';
import {
  CategorieUm,
  UnitaDiMisura,
  UnitaDiMisuraService,
} from 'src/app/services/unita-di-misura/unita-di-misura.service';
import { UtenteService } from 'src/app/services/utente/utente.service';
import { TipoElemento, UtilityService } from 'src/app/services/utility/utility.service';
import { DialogTarghetStdrComponent } from '../dialog-targhet-stdr/dialog-targhet-stdr.component';

@Component({
  selector: 'app-dialog-crea-kpi',
  templateUrl: './dialog-crea-kpi.component.html',
  styleUrls: ['./dialog-crea-kpi.component.scss'],
  standalone: false,
})
export class DialogCreaKPIComponent implements AfterViewInit {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  public infiniteRecords: number = Infinity;

  public selectedSettori: string[] = [];
  public selectedCategorie: string[] = [];

  public selectedStdr: Stdr[] = [];

  public selectedUnitaDiMisura: string[] = [];

  public arraySettori: Settore[] = [];
  public arrayCategorie: CategorieUm[] = [];
  public filteredCategorie: Observable<CategorieUm[]> | undefined;

  public arrayUnitaDiMisura: UnitaDiMisura[] = [];

  public mostraNote: boolean = false;

  //Signal per controllare la chiususra della dialog
  closeDialog = signal<boolean>(true);

  /* Form Controls */
  public formKPI = new FormGroup({
    id: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    nomeBreve: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
    descrizione: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
    settori: new FormControl<Settore[]>([], {
      nonNullable: true,
    }),
    stdr: new FormControl<Stdr[]>([], {
      nonNullable: true,
    }),
    compliance: new FormControl<Stdr[]>([], {
      nonNullable: true,
    }),
    idCategoriaUnitaDiMisura: new FormControl<string | CategorieUm>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    idUnitaDiRiferimento: new FormControl<string | UnitaDiMisura>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    note: new FormControl<string>('', {
      nonNullable: true,
      validators: [],
    }),

    tag: new FormControl<string>('', {
      nonNullable: true,
      validators: [],
    }),
    creatoDaContesto: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [],
    }),
    selected: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [],
    }),
    compilato: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [],
    }),
    figura: new FormControl<any>(undefined, {
      nonNullable: true,
      validators: [],
    }),
  });

  constructor(
    public dialogRef: MatDialogRef<DialogCreaKPIComponent>,
    private readonly utilityService: UtilityService,
    private readonly settoriService: SettoriService,
    private readonly kpiService: KPIService,
    private readonly unitaDiMisura: UnitaDiMisuraService,
    private readonly sintesiService: SintesiService,
    private readonly bilancioService: BilancioService,
    private readonly pianoService: PianoService,
    public dialog: MatDialog,
    public utenteService: UtenteService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      kpi: KPI;
      idContest: string;
      idAmbito: string;
      idTematica: string;
      contesto: string;
      idObiettivo: string;
    },
  ) {}

  // Mostro se il kpi è stato creato dal contesto e sono in un contesto || sono in un contesto e sono in creazione nuova di un kpi || sono in libreria
  get isKpiCreatoDaContesto(): boolean {
    const hasContesto = !!this.data?.contesto;

    return (
      (this.data?.kpi?.creatoDaContesto && hasContesto) ||
      (!this.data?.kpi?.id && hasContesto) ||
      !hasContesto
    );
  }

  get isConfigurazione() {
    return !this.data.contesto;
  }

  get idKpi() {
    return this.formKPI.get('id')?.value ?? this.data?.kpi?.id ?? '';
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.datiIniziali();
    });

    const modificoKpiDiLibreriaInContesto =
      !this.isConfigurazione && this.data?.kpi?.id && !this.data?.kpi?.creatoDaContesto;

    if (modificoKpiDiLibreriaInContesto) {
      this.formKPI.get('descrizione')?.disable();
    }
  }

  compareSettori(settore1: Settore, settore2: Settore): boolean {
    return settore1.id === settore2.id; // Sostituisci "id" con la proprietà univoca dei settori
  }
  compareCategorie(categoria1: CategorieUm, categoria2: CategorieUm): boolean {
    return categoria1.id === categoria2.id; // Sostituisci "id" con la proprietà univoca delle categorie
  }
  compareUnitaDiMisura(unitaDiMiusra1: UnitaDiMisura, unitaDiMiusra2: UnitaDiMisura): boolean {
    return unitaDiMiusra1.id === unitaDiMiusra2.id; // Sostituisci "id" con la proprietà unitaDiMisura
  }

  mostraNoteFn() {
    this.mostraNote = !this.mostraNote;
  }

  /**
   *  Dati iniziali
   */
  private datiIniziali() {
    this.spinnerOver.show();

    this._getSettori();
    this._getCategorie().subscribe((result) => {
      if (result) {
        if (this.data?.kpi) {
          Object.keys(this.data.kpi).forEach((value) => {
            if (value === 'idUnitaDiRiferimento') {
              return; // si salta l'unita di riferimento perchè viene valorizzata dentro idCategoriaUnitaDiMisura
            }
            if (value === 'idCategoriaUnitaDiMisura') {
              const selectedCategoriaId = this.data.kpi.idCategoriaUnitaDiMisura;
              const selectedUnitaDiMisuraId = this.data.kpi.idUnitaDiRiferimento;
              const selectedCategoria = this.arrayCategorie.find(
                (categoria) => categoria.id === selectedCategoriaId,
              );

              if (selectedCategoria && selectedCategoria.nome) {
                this.formKPI.get('idCategoriaUnitaDiMisura')?.setValue(selectedCategoria);

                this.formKPI.controls.idUnitaDiRiferimento.enable();
                this.arrayUnitaDiMisura = selectedCategoria.unitaDiMisura || [];

                const selectedUnitaMisura = this.arrayUnitaDiMisura.find(
                  (unitaMisura) => unitaMisura.id === selectedUnitaDiMisuraId,
                );
                if (selectedUnitaMisura) {
                  this.formKPI.get('idUnitaDiRiferimento')?.setValue(selectedUnitaMisura);
                }
              }
            } else {
              this.formKPI.get(value)?.setValue((this.data.kpi as any)[value]);
            }
          });
          this.selectedStdr = this.data.kpi.stdr || [];
          this.permessi();
        }

        if (this.data.contesto === 'sintesi' || this.data.contesto === 'bilancio') {
          this.formKPI
            .get('descrizione')
            ?.addValidators([Validators.required, UtilityService.blankValidator]);
          this.formKPI.get('idUnitaDiRiferimento')?.addValidators([Validators.required]);
          this.formKPI.get('idCategoriaUnitaDiMisura')?.addValidators([Validators.required]);
        }
      }

      this.spinnerOver.hide();
    });
  }

  /**
   * Metodo salvataggio chiamato da HTML
   */
  public async salvaInHtml() {
    this.closeDialog.set(true);
    await this.salva();
    if (this.closeDialog()) this.dialogRef.close();
  }

  /**
   * Esegue il processo completo di salvataggio del Kpi.
   *
   * Il metodo:
   * - valida il form
   * - costruisce l'oggetto di salvataggio
   * - salva in contesto (sintesi, bilancio, policy, piano) oppure tramite il servizio standard
   * - mostra snackbar o dialog di errore
   * @returns un boolean con l'esito
   */
  private async salva(): Promise<boolean> {
    if (!this.formKPI.valid) {
      this.markFormControlsAsTouched();
      return false;
    }

    try {
      const objSalvataggio = this.buildPayload();

      if (this.data.idContest) {
        delete objSalvataggio.infoConsigliatoObbl;
        await this.salvaInContesto(objSalvataggio);
        return true;
      }

      this.spinnerOver.show();

      if (this.idKpi) {
        await this.updateKpi(objSalvataggio);
      } else {
        await this.createKpi(objSalvataggio);
      }

      this.utilityService.openSnackBar('Salvataggio effettuato', '', {
        duration: 2000,
        panelClass: ['success-snackbar'],
      });
      return true;
    } catch (error) {
      console.error('Errore durante il salvataggio dei kpi:', error);
      this.utilityService.openDialog({
        titolo: 'Attenzione',
        descrizione: 'Errore nel salvataggio dei kpi',
        bottoni: [{ nome_btn: 'Chiudi' }],
      });
      this.closeDialog.set(false);
      return false;
    } finally {
      this.spinnerOver.hide();
    }
  }

  /**
   * Imposta tutti i controlli del form come "touched".
   *
   * Utilizzato quando il form non è valido, per forzare la visualizzazione
   * degli errori di validazione nei campi.
   */
  private markFormControlsAsTouched(): void {
    Object.values(this.formKPI.controls).forEach((control) => control.markAsTouched());
    this.closeDialog.set(false);
  }

  /**
   * Crea il payload normalizzato da inviare
   * @returns {any} L'oggetto completo per la chiamata alla API.
   */
  private buildPayload(): any {
    let formKpis = this.formKPI.getRawValue();
    return {
      ...(this.data.idContest ? this.data.kpi : {}),
      ...formKpis,
      id: formKpis.id ?? '',
      idCategoriaUnitaDiMisura:
        (formKpis.idCategoriaUnitaDiMisura as CategorieUm | undefined)?.id ?? '',
      idUnitaDiRiferimento: (formKpis.idUnitaDiRiferimento as UnitaDiMisura | undefined)?.id ?? '',
    };
  }

  /**
   * Effettua la chiamata API per aggiornare una Kpi esistente.
   *
   * @param id - L'ID dell'Kpi da aggiornare.
   * @param kpi - L'oggetto dati da inviare alla API.
   */
  private async updateKpi(kpi: any): Promise<void> {
    await firstValueFrom(this.kpiService.putKPI(kpi));
  }

  /**
   * Effettua la chiamata API per creare una nuova Kpi.
   *
   * @param obj - L'oggetto dati da inviare alla API.
   */
  private async createKpi(obj: any): Promise<void> {
    const result = await firstValueFrom(this.kpiService.postKPI(obj));
    if (result) {
      this.formKPI.get('id')?.setValue(result.body);
    }
  }

  /**
   * Metodo che mi salva una kpi effimero in contesto
   * @returns
   */
  private async salvaInContesto(objSalvataggio: any): Promise<void> {
    if (!this.data.idAmbito) {
      this.mostraMessaggio('Attenzione', 'Ambito non trovato o non riconosciuto');
      return;
    }

    if (!this.data.idTematica) {
      this.mostraMessaggio('Attenzione', 'Tematica non trovata o non riconosciuta');
      return;
    }

    if (!this.idKpi) {
      this.formKPI.get('creatoDaContesto')?.setValue(true);
    }

    const serviceCall = this.getServiceCallForContesto(objSalvataggio);
    if (!serviceCall) return;

    try {
      this.spinnerOver.show();
      const result = await firstValueFrom(serviceCall);
      this.mostraMessaggioSnackbar('Salvataggio effettuato');
      this.closeDialog.set(false);
      this.dialogRef.close(result);
    } catch (err) {
      console.error('Errore durante il salvataggio del kpi:', err);
      this.mostraMessaggio('Attenzione', 'Errore nel salvataggio del kpi');
    } finally {
      this.spinnerOver.hide();
    }
  }

  /**
   * Mi prendo il servizio corretto da chiamare in base al contesto in cui sono
   * @param objSalvataggio oggetto da salvare da passare come parametro agli EP
   * @returns
   */
  private getServiceCallForContesto(objSalvataggio: any) {
    switch (this.data.contesto) {
      case 'sintesi':
        return this.sintesiService.putKpiEffimeri(
          this.data.idContest,
          this.data.idAmbito,
          this.data.idTematica,
          objSalvataggio,
        );
      case 'bilancio':
        return this.bilancioService.putKpiEffimeri(
          this.data.idContest,
          this.data.idAmbito,
          this.data.idTematica,
          objSalvataggio,
        );

      case 'piano':
        return this.pianoService.putTargetQuantitativiEffimeri(
          this.data.idContest,
          this.data.idAmbito,
          this.data.idTematica,
          this.data.idObiettivo,
          objSalvataggio,
        );
      default:
        console.warn('Contesto non riconosciuto:', this.data.contesto);
        return null;
    }
  }

  private mostraMessaggio(titolo: string, descrizione: string) {
    this.utilityService.openDialog({
      titolo: titolo,
      descrizione: descrizione,
      bottoni: [{ nome_btn: 'Chiudi' }],
    });
  }

  private mostraMessaggioSnackbar(messaggio: string) {
    this.utilityService.openSnackBar(messaggio, '', {
      duration: 2000,
      panelClass: ['success-snackbar'],
    });
  }

  /**
   * Metodo che prende gli stdr
   */
  private _getSettori() {
    this.settoriService
      .getSettori(
        0,
        1000,
        '',
        [],
        [
          {
            chiave: 'titolo',
            sort: 'desc',
          },
        ],
      )
      .subscribe({
        next: (result: any) => {
          this.arraySettori = result?.content || [];
        },
        error: (err: any) => {
          console.error('Errore durante il caricamento dei settori:', err);

          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: 'Errore nel recupero dei settori',
            bottoni: [{ nome_btn: 'Ok' }],
          });
        },
      });
  }

  async opeDialogTarget() {
    try {
      const esito = await this.salva();

      if (!esito) {
        this.utilityService.openDialog({
          titolo: 'Attenzione',
          descrizione: 'Compila tutti i campi obbligatori',
          bottoni: [{ nome_btn: 'Ok' }],
        });

        console.error(esito);
        return;
      }

      const dialog = this.dialog.open(DialogTarghetStdrComponent, {
        data: {
          idKpi: this.idKpi,
          stdr: this.data?.kpi?.stdr || [],
          idsSettori: this.formKPI.get('settori')?.value?.map((set) => set.id) || [],
        },
        panelClass: ['dialog-container'],
        disableClose: false,
        width: '80%',
        height: '95%',
        autoFocus: false,
      });
      dialog.afterClosed().subscribe((risp) => {
        if (risp) {
          this.selectedStdr = [];
          this.selectedStdr.push(...risp?.compliance);
          this.selectedStdr.push(...risp?.stdr);

          this.data.kpi.stdr = this.selectedStdr;
          this.formKPI.get('stdr')?.setValue(risp?.stdr);
          this.formKPI.get('compliance')?.setValue(risp?.compliance);
        }
      });
    } catch (error) {
      console.error('Errore durante il salvataggio del kpi:', error);
    }
  }

  /**
   * Metodo che prende le categorie
   */
  private _getCategorie() {
    return this.unitaDiMisura.getCategoria().pipe(
      map((resultCategoria: CategorieUm[]) => {
        this.arrayCategorie = resultCategoria || [];

        return true;
      }),
      catchError((err: any) => {
        console.error('Errore durante il caricamento delle Categorie:', err);

        this.utilityService.openDialog({
          titolo: 'Attenzione',
          descrizione: 'Errore nel recupero delle Categorie',
          bottoni: [{ nome_btn: 'Ok' }],
        });
        return of(false);
      }),
    );
  }

  displayFn(categoria: CategorieUm): string {
    return categoria?.nome ? categoria?.nome : '';
  }

  getCategorie = (id: any, txtRicerca: string) => {
    let records: CategorieUm[] = [];

    if (id) {
      records = this.arrayCategorie.filter((option) => option.id?.toLowerCase() === id);
    } else if (txtRicerca) {
      records = this._filter(txtRicerca as string);
    } else {
      records = this.arrayCategorie.slice();
    }

    return of(records);
  };

  private _filter(categoria: string): CategorieUm[] {
    const filterValue = categoria.toLowerCase();
    return this.arrayCategorie.filter((option) => option.nome?.toLowerCase().includes(filterValue));
  }

  /**
   * Metodo che prende le unità di misura
   */
  public getUnitaDiMisura(record: CategorieUm) {
    if (this.formKPI.get('idUnitaDiRiferimento')?.status === 'DISABLED') {
      return;
    }

    this.formKPI.controls.idUnitaDiRiferimento.enable();
    this.arrayUnitaDiMisura = record.unitaDiMisura || [];

    const umVal = this.formKPI.get('idUnitaDiRiferimento')?.value;
    if (umVal && typeof umVal === 'object') {
      const trovato = this.arrayUnitaDiMisura.find((um) => um.id === umVal.id);
      if (!trovato) {
        this.formKPI.get('idUnitaDiRiferimento')?.setValue('');
      }
    }
  }

  /**
   * Se sono professionista e il kpi è di libreria allora proteggo i campi.
   */
  private permessi() {
    if (
      !this.utenteService.isPermessoAttivo('EDIT_UNITA_MISURA') &&
      !this.data.kpi.creatoDaContesto
    ) {
      /*     this.formKPI.get('idUnitaDiRiferimento')?.disable(); */
      this.formKPI.get('idCategoriaUnitaDiMisura')?.disable();
    }
  }

  /**
   * Metodo chiamato da html per la dialog prompt ai
   * prima salvo l'oggetto e poi apri la dialog
   */
  public async ctrOpenPromptAI() {
    this.closeDialog.set(false);

    if (!this.idKpi) {
      await this.salva();
    }
    if (this.idKpi) {
      this.promptAI();
    }
  }

  /**
   * Dialog per inserimneto prompt
   */
  private promptAI() {
    const tipoElemento: TipoElemento = 'KPI';
    const dialogPromptAi = this.dialog.open(DialogPromptAiComponent, {
      data: {
        tipoElemento,
        idElemento: this.idKpi,
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: '50%',
      maxHeight: '95%',
      autoFocus: false,
    });

    dialogPromptAi.afterClosed().subscribe((result) => {
      //
    });
  }
}
